import React, { useState } from "react";
// ANT DESIGN COMPONENTS
import { Button, Checkbox, Drawer, Input, Select, Slider, Space } from "antd";
import BufferedNumberInput from "./BufferedNumberInput";
import {
  DEFAULT_BACKGROUND_OPACITY,
  DEFAULT_DRAWER_WIDTH,
  DEFAULT_ROTATION,
  DEFAULT_SYNC_ROTATIONS,
  DEFAULT_WAYPOINT_BACKGROUND_SIZE,
  DEFAULT_FONT_SIZE,
  MIN_SHAPE_SIZE,
} from "./constants";
import type { LiftEditorDrawerProps } from "./types";

function LiftEditorDrawer({
  open,
  onClose,
  onSave,
  onDelete,
  loading = false,
  waypoint,
  mode,
  isPinMode,
  lockAspectRatio = false,
  availableLiftOptions = [],
  onWaypointChange,
  onTogglePinMode,
}: LiftEditorDrawerProps) {
  const [showViewOptions, setShowViewOptions] = useState<boolean>(false);
  const hasNickname = Boolean(waypoint.nickname && waypoint.nickname.trim().length > 0);
  const backgroundRotationDeg = waypoint.backgroundRotationDeg ?? DEFAULT_ROTATION;
  const iconRotationDeg = waypoint.iconRotationDeg ?? DEFAULT_ROTATION;
  const nameRotationDeg = waypoint.nameRotationDeg ?? DEFAULT_ROTATION;
  const syncRotations = waypoint.syncRotations ?? DEFAULT_SYNC_ROTATIONS;
  const shouldLockAspectRatio = lockAspectRatio && (waypoint.maintainIconAspectRatio ?? true);

  const getSizeLockedToWidth = (nextWidth: number) => {
    const currentWidth = Math.max(waypoint.backgroundWidth, MIN_SHAPE_SIZE);
    const currentHeight = Math.max(waypoint.backgroundHeight, MIN_SHAPE_SIZE);
    const ratio = currentHeight / currentWidth;

    return {
      backgroundWidth: nextWidth,
      backgroundHeight: Math.max(MIN_SHAPE_SIZE, Math.round(nextWidth * ratio)),
    };
  };

  const getSizeLockedToHeight = (nextHeight: number) => {
    const currentWidth = Math.max(waypoint.backgroundWidth, MIN_SHAPE_SIZE);
    const currentHeight = Math.max(waypoint.backgroundHeight, MIN_SHAPE_SIZE);
    const ratio = currentWidth / currentHeight;

    return {
      backgroundWidth: Math.max(MIN_SHAPE_SIZE, Math.round(nextHeight * ratio)),
      backgroundHeight: nextHeight,
    };
  };

  return (
    <Drawer
      title={mode === "edit" ? "Edit Waypoint" : "Add Lift"}
      placement="right"
      onClose={onClose}
      open={open}
      mask={false}
      width={DEFAULT_DRAWER_WIDTH}
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          {mode === "edit" && onDelete && (
            <Button danger onClick={onDelete} disabled={loading}>
              Delete
            </Button>
          )}
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={onSave}
            disabled={!hasNickname || loading}
            loading={loading}
          >
            Save
          </Button>
        </div>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <div>
          <div style={{ marginBottom: 8 }}>
            Nickname <span style={{ color: "#ff4d4f" }}>*</span>
          </div>
          <Input
            placeholder="Lift nickname"
            value={waypoint.nickname}
            disabled={loading}
            onChange={(event) => {
              const value = event.target.value;
              onWaypointChange({
                ...waypoint,
                nickname: value || undefined,
              });
            }}
            status={hasNickname ? "" : "error"}
          />
          {!hasNickname && (
            <div style={{ marginTop: 6, color: "#ff4d4f", fontSize: 12 }}>
              Nickname is required.
            </div>
          )}
        </div>
        <div>
          <div style={{ marginBottom: 8 }}>Linked Lift</div>
          <Select
            showSearch
            allowClear
            placeholder="Select lift"
            value={waypoint.linkedLift || undefined}
            options={availableLiftOptions}
            disabled={loading}
            optionFilterProp="label"
            onChange={(value) => {
              onWaypointChange({
                ...waypoint,
                linkedLift: value || undefined,
              });
            }}
            style={{ width: "100%" }}
          />
        </div>

        <div>
          <div style={{ marginBottom: 8, fontWeight: 600 }}>Waypoint Anchor</div>
          <Space size="small" style={{ width: "100%", alignItems: "flex-end" }}>
            <div>
              <div style={{ marginBottom: 6 }}>x</div>
              <BufferedNumberInput
                style={{ width: 92 }}
                value={waypoint.x}
                fallback={0}
                onCommit={(value) => {
                  onWaypointChange({ ...waypoint, x: value });
                }}
                placeholder="x"
              />
            </div>
            <div>
              <div style={{ marginBottom: 6 }}>y</div>
              <BufferedNumberInput
                style={{ width: 92 }}
                value={waypoint.y}
                fallback={0}
                onCommit={(value) => {
                  onWaypointChange({ ...waypoint, y: value });
                }}
                placeholder="y"
              />
            </div>
            <Button
              type={isPinMode ? "primary" : "default"}
              onClick={onTogglePinMode}
              disabled={loading}
            >
              Click to Pin
            </Button>
          </Space>

          <div style={{ marginTop: 10 }}>
            <Button
              type="primary"
              onClick={() => {
                setShowViewOptions((previous) => !previous);
              }}
              disabled={loading}
            >
              View Options
            </Button>
          </div>

          {showViewOptions && (
            <div
              style={{
                marginTop: 12,
                padding: 12,
                border: "1px solid #d9d9d9",
                borderRadius: 8,
                backgroundColor: "#fafafa",
              }}
            >
              <Space direction="vertical" size="small" style={{ width: "100%" }}>
                <div>
                  <div style={{ marginBottom: 6 }}>Background Color</div>
                  <input
                    type="color"
                    value={waypoint.backgroundColorHex}
                    disabled={loading}
                    onChange={(event) => {
                      onWaypointChange({
                        ...waypoint,
                        backgroundColorHex: event.target.value,
                      });
                    }}
                    style={{
                      width: 52,
                      height: 32,
                      border: "none",
                      padding: 0,
                      background: "none",
                    }}
                  />
                </div>

                <div>
                  <div style={{ marginBottom: 6 }}>Transparency (%)</div>
                  <Slider
                    min={0}
                    max={100}
                    value={waypoint.backgroundOpacity}
                    disabled={loading}
                    onChange={(value) => {
                      onWaypointChange({
                        ...waypoint,
                        backgroundOpacity:
                          typeof value === "number" ? value : DEFAULT_BACKGROUND_OPACITY,
                      });
                    }}
                    tooltip={{ formatter: (value) => `${value ?? 0}%` }}
                  />
                </div>

                <Space size="small" style={{ width: "100%" }}>
                  <div>
                    <div style={{ marginBottom: 6 }}>Width (px)</div>
                    <BufferedNumberInput
                      min={MIN_SHAPE_SIZE}
                      value={waypoint.backgroundWidth}
                      fallback={DEFAULT_WAYPOINT_BACKGROUND_SIZE}
                      onCommit={(value) => {
                        if (shouldLockAspectRatio) {
                          onWaypointChange({
                            ...waypoint,
                            ...getSizeLockedToWidth(value),
                          });
                          return;
                        }

                        onWaypointChange({
                          ...waypoint,
                          backgroundWidth: value,
                        });
                      }}
                      style={{ width: 110 }}
                    />
                  </div>
                  <div>
                    <div style={{ marginBottom: 6 }}>Height (px)</div>
                    <BufferedNumberInput
                      min={MIN_SHAPE_SIZE}
                      value={waypoint.backgroundHeight}
                      fallback={DEFAULT_WAYPOINT_BACKGROUND_SIZE}
                      onCommit={(value) => {
                        if (shouldLockAspectRatio) {
                          onWaypointChange({
                            ...waypoint,
                            ...getSizeLockedToHeight(value),
                          });
                          return;
                        }

                        onWaypointChange({
                          ...waypoint,
                          backgroundHeight: value,
                        });
                      }}
                      style={{ width: 110 }}
                    />
                  </div>
                </Space>

                <div
                  style={{
                    marginTop: 8,
                    padding: 10,
                    border: "1px solid #e5e7eb",
                    borderRadius: 6,
                    backgroundColor: "#ffffff",
                  }}
                >
                  <div style={{ marginBottom: 8, fontWeight: 600 }}>Rotations</div>

                  <div style={{ marginBottom: 10 }}>
                    <Checkbox
                      checked={syncRotations}
                      disabled={loading}
                      onChange={(event) => {
                        onWaypointChange({
                          ...waypoint,
                          syncRotations: event.target.checked,
                        });
                      }}
                    >
                      Keep All Rotations Synced
                    </Checkbox>
                  </div>

                  <Space size="small" style={{ width: "100%", alignItems: "flex-end" }}>
                    <div>
                      <div style={{ marginBottom: 6 }}>Background (deg)</div>
                      <BufferedNumberInput
                        min={-360}
                        max={360}
                        value={backgroundRotationDeg}
                        disabled={loading}
                        fallback={DEFAULT_ROTATION}
                        onCommit={(nextRotation) => {
                          if (syncRotations) {
                            onWaypointChange({
                              ...waypoint,
                              backgroundRotationDeg: nextRotation,
                              iconRotationDeg: nextRotation,
                              nameRotationDeg: nextRotation,
                            });
                            return;
                          }

                          onWaypointChange({
                            ...waypoint,
                            backgroundRotationDeg: nextRotation,
                          });
                        }}
                        style={{ width: 130 }}
                      />
                    </div>

                    <div>
                      <div style={{ marginBottom: 6 }}>Icon (deg)</div>
                      <BufferedNumberInput
                        min={-360}
                        max={360}
                        value={iconRotationDeg}
                        disabled={loading}
                        fallback={DEFAULT_ROTATION}
                        onCommit={(nextRotation) => {
                          if (syncRotations) {
                            onWaypointChange({
                              ...waypoint,
                              backgroundRotationDeg: nextRotation,
                              iconRotationDeg: nextRotation,
                              nameRotationDeg: nextRotation,
                            });
                            return;
                          }

                          onWaypointChange({
                            ...waypoint,
                            iconRotationDeg: nextRotation,
                          });
                        }}
                        style={{ width: 130 }}
                      />
                    </div>
                  </Space>

                  <div style={{ marginTop: 10 }}>
                    <div style={{ marginBottom: 6 }}>Name Header (deg)</div>
                    <BufferedNumberInput
                      min={-360}
                      max={360}
                      value={nameRotationDeg}
                      disabled={loading}
                      fallback={DEFAULT_ROTATION}
                      onCommit={(nextRotation) => {
                        if (syncRotations) {
                          onWaypointChange({
                            ...waypoint,
                            backgroundRotationDeg: nextRotation,
                            iconRotationDeg: nextRotation,
                            nameRotationDeg: nextRotation,
                          });
                          return;
                        }

                        onWaypointChange({
                          ...waypoint,
                          nameRotationDeg: nextRotation,
                        });
                      }}
                      style={{ width: 130 }}
                    />
                  </div>
                </div>

                <div>
                  <Checkbox
                    checked={waypoint.displayName}
                    disabled={loading}
                    onChange={(event) => {
                      onWaypointChange({
                        ...waypoint,
                        displayName: event.target.checked,
                      });
                    }}
                  >
                    Display Name
                  </Checkbox>
                </div>

                {waypoint.displayName && (
                  <Space size="small" style={{ width: "100%", alignItems: "flex-start" }}>
                    <div>
                      <div style={{ marginBottom: 6 }}>Font Size (px)</div>
                      <BufferedNumberInput
                        min={8}
                        max={48}
                        value={waypoint.fontSize}
                        disabled={loading}
                        fallback={DEFAULT_FONT_SIZE}
                        onCommit={(value) => {
                          onWaypointChange({
                            ...waypoint,
                            fontSize: value,
                          });
                        }}
                        style={{ width: 110 }}
                      />
                    </div>
                    <div>
                      <div style={{ marginBottom: 6 }}>Font Color</div>
                      <input
                        type="color"
                        value={waypoint.fontColorHex}
                        disabled={loading}
                        onChange={(event) => {
                          onWaypointChange({
                            ...waypoint,
                            fontColorHex: event.target.value,
                          });
                        }}
                        style={{
                          width: 52,
                          height: 24,
                          border: "none",
                          padding: 0,
                          background: "none",
                          display: "block",
                        }}
                      />
                    </div>
                  </Space>
                )}
              </Space>
            </div>
          )}
        </div>
      </Space>
    </Drawer>
  );
}

export default LiftEditorDrawer;
